import { useEffect, useMemo, useState } from 'react';

import { RootState } from '@iwProvider/configureStore';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { AddressDetails } from './addressDetails/AddressDetails';
import ContactDetails from './contactDetails/ContactDetails';
import PersonDetails from './personDetails/PersonDetails';
import {
    ADDRESS_FETCH_REQUESTED,
    DELETE_PERSON,
    PERSON_FORM_SUBMIT,
    TOGGLE_MANUAL_ADDRESS_STATE
} from './PersonModal.actions';
import styles from './PersonModal.module.css';
import { TPersonFormValues } from './PersonModal.types';
import { usePersonForm } from './usePersonForm';
import { getApplicationStatus } from '../../../api/lendingAPI/ApplicationSelector';
import {
    Person as TDeserialisedPerson,
    getPersonFromUID,
    isDirector,
    isShareholder
} from '../../../api/lendingAPI/CustomerStateAPIHelper';
import { FormattedName } from '../../../iwIntlProvider/FormattedName';
import { CLOSE_MODAL } from '../../../iwModals/Modals.actions';
import ConfirmationButton from '../../ui/confirmationButton/ConfirmationButton';
import { CancelAction } from '../../ui/confirmationResponse/confirmationQuestion/cancelAction/CancelAction';
import { ConfirmAction } from '../../ui/confirmationResponse/confirmationQuestion/confirmAction/ConfirmAction';
import { ConfirmationQuestion } from '../../ui/confirmationResponse/confirmationQuestion/ConfirmationQuestion';
import { ConfirmationResponse } from '../../ui/confirmationResponse/ConfirmationResponse';
import { ConfirmationStatement } from '../../ui/confirmationResponse/confirmationStatement/ConfirmationStatement';
import { Prompt } from '../../ui/prompt';

function toDeserialisedPerson(
    data: TPersonFormValues,
    roles: TDeserialisedPerson['roles']
): TDeserialisedPerson {
    // RHF sets unregistered text inputs to "" instead of undefined.
    // For NEW addresses (no uid), strip empty strings so serialiseAddresses omits them.
    // For EXISTING addresses (have uid), preserve empty strings as they came from the API.
    const addresses = data.addresses.map((addr) => {
        if (addr.uid) return addr;
        const cleaned: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(addr)) {
            if (value !== '') cleaned[key] = value;
        }
        return cleaned;
    });

    return {
        uid: data.uid || '',
        title: data.title,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email || '',
        emailUID: data.emailUID || '',
        dateOfBirth: data.dateOfBirth as {
            day: string;
            month: string;
            year: string;
        },
        roles,
        isApplicant: !!data.isApplicant,
        isGuarantor: !!data.isGuarantor,
        isDirector: !!data.isDirector,
        isShareholder: !!data.isShareholder,
        additionalGuarantorConsent: data.additionalGuarantorConsent,
        marketing: !!data.marketing,
        phones: data.phones,
        addresses: addresses as TDeserialisedPerson['addresses']
    };
}

const mapStateToProps = (state: RootState) => {
    const { api, personModal } = state;
    const person = getPersonFromUID(api.customer_state.data, personModal.uid!);
    return {
        ...personModal,
        people: api.customer_state.data.application.people,
        hasCreditDecision: getApplicationStatus(state) !== 'open',
        initialIsShareholder: isShareholder(person),
        initialIsDirector: isDirector(person),
        companyType: state.profile.companyType.value
    };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    closeModal: () => dispatch({ type: CLOSE_MODAL, id: 'PersonModal' }),
    submitPersonForm: (person: TDeserialisedPerson) =>
        dispatch({ type: PERSON_FORM_SUBMIT, person }),
    deletePerson: (uid: string) => dispatch({ type: DELETE_PERSON, uid }),
    fetchAddresses: (postcode: string) =>
        dispatch({ type: ADDRESS_FETCH_REQUESTED, postcode }),
    toggleManualAddressState: (addressIndex: number) =>
        dispatch({ type: TOGGLE_MANUAL_ADDRESS_STATE, addressIndex })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

const PersonModal = ({
    submitPersonForm,
    deletePerson,
    uid,
    isEditingPerson,
    firstName,
    lastName,
    isApplicant,
    isGuarantor: initialIsGuarantorFromRedux,
    personFormSubmitting,
    closeModal,
    people,
    hasCreditDecision,
    initialIsShareholder,
    initialIsDirector,
    phones,
    addresses,
    showManualAddresses,
    fetchAddresses,
    toggleManualAddressState,
    companyType,
    roles,
    ...rest
}: PropsFromRedux) => {
    const intl = useIntl();

    const defaultValues: TPersonFormValues = useMemo(
        () => ({
            uid,
            title: rest.title,
            firstName,
            lastName,
            email: rest.email,
            emailUID: rest.emailUID,
            dateOfBirth: rest.dateOfBirth || {
                day: null,
                month: null,
                year: null
            },
            isDirector: initialIsDirector,
            isShareholder: initialIsShareholder,
            isGuarantor: initialIsGuarantorFromRedux,
            isApplicant,
            additionalGuarantorConsent: undefined,
            marketing: rest.marketing,
            phones: phones?.length ? phones : [{}],
            addresses: addresses?.length
                ? addresses
                : [{ isPrimaryAddress: true }]
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [] // Only compute once on mount
    );

    const { register, control, watch, setValue, formState, handleSave } =
        usePersonForm(defaultValues, people, intl);

    const { errors } = formState;

    const [isGuarantor, setIsGuarantor] = useState(
        !!initialIsGuarantorFromRedux
    );

    // Watch isGuarantor changes
    useEffect(() => {
        const subscription = watch((value, { name }) => {
            if (name === 'isGuarantor') {
                setIsGuarantor(!!value.isGuarantor);
            }
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    const onValid = (data: TPersonFormValues) => {
        submitPersonForm(toDeserialisedPerson(data, roles || []));
    };

    const handleDeletePerson = () => {
        deletePerson(uid!);
    };

    const renderTitle = () => {
        if (!isEditingPerson) {
            return (
                <FormattedMessage
                    id="_.person.title"
                    defaultMessage="Add a person"
                />
            );
        } else {
            const name = (
                <FormattedName
                    firstName={firstName}
                    lastName={lastName}
                    possessive={true}
                />
            );

            return (
                <FormattedMessage
                    id="_.edit_person.title"
                    defaultMessage="Edit {name} details"
                    values={{ name }}
                />
            );
        }
    };

    const renderDeletePerson = () => {
        const name = (
            <FormattedName firstName={firstName} lastName={lastName} />
        );

        if (isApplicant || isGuarantor || !isEditingPerson) {
            return null;
        }

        return (
            <ConfirmationResponse
                className={styles.deletePerson}
                onSubmit={handleDeletePerson}
            >
                <ConfirmationStatement>
                    <FormattedMessage
                        id="_.person.delete_person"
                        defaultMessage="Remove {name}"
                        values={{ name }}
                    />
                </ConfirmationStatement>

                <ConfirmationQuestion>
                    <FormattedMessage
                        id="_.person.delete_confirmation"
                        defaultMessage="Are you sure?"
                    />

                    <ConfirmAction>
                        <FormattedMessage
                            id="_.person.confirm_delete"
                            defaultMessage="Yes"
                        />
                    </ConfirmAction>
                    <CancelAction>
                        <FormattedMessage
                            id="_.person.cancel_delete"
                            defaultMessage="No"
                        />
                    </CancelAction>
                </ConfirmationQuestion>
            </ConfirmationResponse>
        );
    };

    return (
        <div className={styles.PersonModal}>
            <div className={styles.title} data-testid="title">
                {renderTitle()}
                {renderDeletePerson()}
            </div>

            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSave(onValid);
                }}
                autoComplete="off"
                data-testid="person-form"
            >
                <PersonDetails
                    register={register}
                    control={control}
                    errors={errors}
                    isGuarantor={isGuarantor}
                    isEditingPerson={!!isEditingPerson}
                    hasCreditDecision={hasCreditDecision}
                />

                <ContactDetails
                    register={register}
                    control={control}
                    errors={errors}
                    isGuarantor={isGuarantor}
                    isApplicant={!!isApplicant}
                    isEditingPerson={!!isEditingPerson}
                    hasCreditDecision={hasCreditDecision}
                />

                <AddressDetails
                    register={register}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    watch={watch}
                    isGuarantor={isGuarantor}
                    isApplicant={!!isApplicant}
                    isEditingPerson={!!isEditingPerson}
                    hasCreditDecision={hasCreditDecision}
                    showManualAddresses={showManualAddresses}
                    fetchAddresses={fetchAddresses}
                    toggleManualAddressState={toggleManualAddressState}
                    fetchedAddresses={addresses || []}
                    companyType={companyType}
                />

                <Prompt className={styles.prompt}>
                    <button type="button" onClick={closeModal}>
                        <FormattedMessage
                            id="_.your_company.prompt.cancel"
                            defaultMessage="Cancel"
                        />
                    </button>
                    <ConfirmationButton
                        loading={personFormSubmitting}
                        type="submit"
                    >
                        <FormattedMessage
                            id="_.your_company.prompt.save"
                            defaultMessage="Save"
                        />
                    </ConfirmationButton>
                </Prompt>
            </form>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, mapDispatchToProps)(PersonModal);
